
import { useCallback, useEffect, useState } from 'react';
import { httpClient } from '@/lib/http-client';
import { getEndpointUrl } from '@/lib/api-config';
import { authService } from '@/lib/auth';
import { useAuth } from '@/contexts/AuthContext';
import { useAdelantoCart } from '@/contexts/AdelantoCartContext';
import MelBalanceWidget from './MelBalanceWidget';

type MelDirection = 'debit' | 'credit' | 'informative';

type MelMovementType =
  | 'debit_advance_approved'
  | 'credit_installment_collected'
  | 'credit_advance_cancelled'
  | 'credit_admin_adjustment'
  | 'debit_admin_adjustment'
  | 'cambio_mel_vigente';

interface LedgerRow {
  id: string;
  createdAt: string;
  movementType: MelMovementType;
  direction: MelDirection;
  amount: number;
  advanceRequestId: string | null;
  installmentDetailId: string | null;
  note: string | null;
}

interface LedgerResponse {
  data: LedgerRow[];
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
}

const MOVEMENT_LABELS: Record<MelMovementType, string> = {
  debit_advance_approved: 'Adelanto aprobado',
  credit_installment_collected: 'Cuota cobrada',
  credit_advance_cancelled: 'Adelanto cancelado',
  credit_admin_adjustment: 'Ajuste manual (crédito)',
  debit_admin_adjustment: 'Ajuste manual (débito)',
  cambio_mel_vigente: 'Cambio de línea',
};

const formatCLP = (n: number) => n.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 });
const formatDate = (iso: string) => {
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export default function MelCartolaPanel() {
  const { user } = useAuth();
  const [response, setResponse] = useState<LedgerResponse | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [movementType, setMovementType] = useState<MelMovementType | ''>('');
  const [fechaDesde, setFechaDesde] = useState<string>('');
  const [fechaHasta, setFechaHasta] = useState<string>('');
  const { registerOnSolicitudConfirmada } = useAdelantoCart();

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const base = getEndpointUrl('mel', 'ledger');
      const qs = new URLSearchParams();
      qs.set('page', String(page));
      qs.set('pageSize', '20');
      if (movementType) qs.set('movementType', movementType);
      if (fechaDesde) qs.set('fechaDesde', fechaDesde);
      if (fechaHasta) qs.set('fechaHasta', fechaHasta);
      const data = await httpClient.get<LedgerResponse>(`${base}?${qs.toString()}`, { shouldCache: false });
      setResponse(data);
    } catch {
      setResponse(null);
    } finally {
      setLoading(false);
    }
  }, [page, movementType, fechaDesde, fechaHasta]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  // Recargar la cartola tras confirmar un adelanto: el backend acaba de
  // escribir un debit_advance_approved que el comercio espera ver en el
  // ledger sin tener que cambiar de página.
  useEffect(() => {
    return registerOnSolicitudConfirmada(() => {
      fetchPage();
    });
  }, [registerOnSolicitudConfirmada, fetchPage]);

  const handleExport = async () => {
    try {
      const base = getEndpointUrl('mel', 'ledger-export');
      const qs = new URLSearchParams({ format: 'csv' });
      if (movementType) qs.set('movementType', movementType);
      if (fechaDesde) qs.set('fechaDesde', fechaDesde);
      if (fechaHasta) qs.set('fechaHasta', fechaHasta);

      const token = authService.getToken();
      // companyId leído del AuthContext en lugar de localStorage para mantenerse
      // en sync con el state actual de sesión (evita exportar con companyId
      // viejo si el contexto rotó pero localStorage no se actualizó aún).
      const companyId = user?.companyId ?? null;

      const res = await fetch(`${base}?${qs.toString()}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          ...(companyId ? { 'x-company-id': companyId } : {}),
        },
      });
      if (!res.ok) throw new Error(`Export falló (${res.status})`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `linea-adelantos-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando CSV', err);
      alert('No se pudo exportar la cartola');
    }
  };

  return (
    <div className="space-y-6">
      <MelBalanceWidget />

      <section className="rounded-xl border bg-card p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">Movimientos de tu línea</h2>
            <p className="mt-1 text-sm text-muted-foreground">Movimientos de tu línea de adelantos: aprobaciones, cuotas cobradas y ajustes.</p>
          </div>
          <button
            type="button"
            onClick={handleExport}
            className="inline-flex cursor-pointer items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm font-medium hover:bg-accent"
          >
            <span className="material-symbols-outlined text-base">download</span>
            Exportar CSV
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-3">
          <select
            value={movementType}
            onChange={(e) => {
              setMovementType(e.target.value as MelMovementType | '');
              setPage(1);
            }}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="">Todos los movimientos</option>
            {Object.entries(MOVEMENT_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <input
            type="date"
            value={fechaDesde}
            max={fechaHasta || undefined}
            onChange={(e) => {
              setFechaDesde(e.target.value);
              setPage(1);
            }}
            placeholder="Desde"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={fechaHasta}
            min={fechaDesde || undefined}
            onChange={(e) => {
              setFechaHasta(e.target.value);
              setPage(1);
            }}
            placeholder="Hasta"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Fecha</th>
                <th className="py-2 pr-3">Movimiento</th>
                <th className="py-2 pr-3">Dirección</th>
                <th className="py-2 pr-3 text-right">Monto</th>
                <th className="py-2 pr-3">Nota</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-muted-foreground">Cargando…</td>
                </tr>
              )}
              {!loading && (response?.data.length ?? 0) === 0 && (
                <tr>
                  <td colSpan={5} className="py-4 text-center text-muted-foreground">Sin movimientos para los filtros seleccionados.</td>
                </tr>
              )}
              {!loading &&
                response?.data.map((row) => (
                  <tr key={row.id} className="border-t border-border">
                    <td className="py-2 pr-3 whitespace-nowrap text-muted-foreground">{formatDate(row.createdAt)}</td>
                    <td className="py-2 pr-3">{MOVEMENT_LABELS[row.movementType] ?? row.movementType}</td>
                    <td className="py-2 pr-3">
                      {row.direction === 'credit' && <span className="text-accent-green">Crédito</span>}
                      {row.direction === 'debit' && <span className="text-red-500">Débito</span>}
                      {row.direction === 'informative' && <span className="text-slate-400">Info</span>}
                    </td>
                    <td className="py-2 pr-3 text-right font-medium">{formatCLP(row.amount)}</td>
                    <td className="py-2 pr-3 text-muted-foreground text-xs max-w-xs truncate">{row.note}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>

        {response && response.pagination.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between text-sm">
            <p className="text-muted-foreground">Página {response.pagination.page} de {response.pagination.totalPages} ({response.pagination.total} movimientos)</p>
            <div className="flex gap-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="rounded-md border px-3 py-1 text-sm disabled:opacity-40"
              >
                Anterior
              </button>
              <button
                disabled={page >= response.pagination.totalPages}
                onClick={() => setPage((p) => p + 1)}
                className="rounded-md border px-3 py-1 text-sm disabled:opacity-40"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
